import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  Switch,
  CodeChip,
  EmptyState,
  Field,
  Input,
  NativeSelect,
  PageHeader,
  Skeleton,
  Table,
  TableShell,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@provenance/design';
import {
  DEFAULT_IMAGE_MARK_WIDTH,
  StampSlotType,
  UserRole,
  coveredMarks,
  isImageMark,
  layerInsertIndex,
  type ChamberIssuePreferences,
  type StampSlot,
  primaryTemplatePage,
  type PageKind,
  withImpliedFace,
  type TemplatePageKinds,
} from '@provenance/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  CheckCircle2,
  GripVertical,
  PenTool,
  Save,
  Trash2,
  Upload,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { SlotTypeBadge, StampCanvas } from '../features/configuration/StampCanvas';
import { toBase64 } from '../features/configuration/upload';
import { ColourField } from '../features/configuration/ColourField';
import { ProofPane } from '../features/configuration/ProofPane';
import { fitTextToContent, markLayout, type MarkLayout, type SampleInputs } from '../features/configuration/coordinates';
import { useImageSizes, useTextMeasurer } from '../features/configuration/measure';
import { useDraftProof } from '../features/configuration/useDraftProof';
import { ApiError, api } from '../lib/api';
import { useSession } from '../lib/session';

interface StampingOverviewRow {
  documentTypeId: string;
  code: string;
  name: string;
  agreement: string | null;
  hasTemplate: boolean;
  configId: string | null;
  configName: string | null;
  slotCount: number;
  sealSlotCount: number;
  certificatesIssued: number;
  hasNumberFormat: boolean;
  readyToIssue: boolean;
}

/**
 * Stamping configuration — the overview.
 *
 * `readyToIssue` is the column that matters. It is the same value the integrator's
 * `GET /document-types` returns, so a chamber can see exactly what SageDocs sees: whether
 * a submission of this type could actually be approved, or would stall in the queue.
 */
export function Stamping() {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['stamping-overview'],
    queryFn: () => api.get<StampingOverviewRow[]>('/api/console/configuration/stamping'),
  });

  const rows = query.data ?? [];
  const notReady = rows.filter((r) => !r.readyToIssue);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Stamping"
        lede="Each document type carries its own seals, signature, certificate number and dates. Drag them onto the page — no coordinates to guess. Uploading the blank form is optional; it only makes the placement tool show the boxes you are aiming between."
      />

      {notReady.length > 0 && (
        <Callout
          tone="warn"
          icon={<AlertTriangle className="size-4" />}
          title={`${notReady.length} document type${notReady.length === 1 ? '' : 's'} cannot be issued yet`}
        >
          A request of one of these types can be lodged but not approved, so it would sit in your
          queue. Integration partners see this too, through the document-types endpoint.
        </Callout>
      )}

      <PrintOnIssueCard />

      {query.isLoading ? (
        <Skeleton className="h-64" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<PenTool className="size-5" />}
          title="No document types enabled"
          description="A platform administrator enables the document types your chamber is accredited to issue."
        />
      ) : (
        <TableShell>
          <Table className="min-w-[820px]">
            <Thead>
              <Tr>
                <Th>Document type</Th>
                <Th>Reference form</Th>
                <Th numeric>Slots</Th>
                <Th numeric>Seals</Th>
                <Th>Numbering</Th>
                <Th numeric>Issued</Th>
                <Th>Ready</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.documentTypeId} onClick={() => navigate(`/configuration/stamping/${row.documentTypeId}`)}>
                  <Td>
                    <span className="font-medium">{row.name}</span>
                    <span className="mt-0.5 block [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                      {row.agreement ?? 'Non-preferential'}
                    </span>
                  </Td>
                  <Td>
                    {/* Not a blocker. The engine stamps the certificate the exporter
                        submitted; the blank form is only what the placement tool draws
                        under the markers, so a missing one is worth knowing, not
                        flagging in amber next to a green Ready. */}
                    {row.hasTemplate ? (
                      <Badge tone="neutral" size="sm">Held</Badge>
                    ) : (
                      <Tooltip content="Placement still works against a blank page of the right size — the form just makes it exact">
                        <span className="[font-size:12.5px] text-[var(--pv-faint)]">Not uploaded</span>
                      </Tooltip>
                    )}
                  </Td>
                  <Td numeric>{row.slotCount || <span className="text-[var(--pv-faint)]">—</span>}</Td>
                  <Td numeric>{row.sealSlotCount || <span className="text-[var(--pv-faint)]">—</span>}</Td>
                  <Td>
                    {row.hasNumberFormat ? (
                      <Badge tone="ok" size="sm">Set</Badge>
                    ) : (
                      <Tooltip content="Without a number format an approval cannot draw a certificate number">
                        <span>
                          <Badge tone="stop" size="sm">Missing</Badge>
                        </span>
                      </Tooltip>
                    )}
                  </Td>
                  <Td numeric>{row.certificatesIssued}</Td>
                  <Td>
                    {row.readyToIssue ? (
                      <Badge tone="ok" size="sm" dot>Ready</Badge>
                    ) : (
                      <Badge tone="warn" size="sm" dot>Not yet</Badge>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableShell>
      )}
    </div>
  );
}

interface StampingDetailData {
  documentType: { id: string; code: string; name: string };
  templateUrl: string | null;
  config: {
    id: string;
    name: string;
    templatePageWidth: number | null;
    templatePageHeight: number | null;
    overleafPages: number[];
    templatePageKinds: TemplatePageKinds | null;
    stampOverleaf: boolean;
    slots: StampSlot[];
  } | null;
  availableSeals: Array<{ id: string; name: string; imageUrl: string | null }>;
  /** What a SIGNATURE mark resolves to, so the canvas shows the real one. */
  signatureUrl: string | null;
  /** What the proof resolves each text mark from, so the canvas prints the same string. */
  sampleInputs: SampleInputs;
}

const SLOT_TYPES: Array<{ value: StampSlotType; label: string; hint: string }> = [
  { value: StampSlotType.SEAL, label: 'Seal', hint: "A seal from your chamber's library" },
  { value: StampSlotType.SIGNATURE, label: 'Signature', hint: "The approving operator's signature image" },
  { value: StampSlotType.CERTIFICATE_NUMBER, label: 'Certificate number', hint: 'Generated at approval' },
  { value: StampSlotType.DATE, label: 'Certificate date', hint: 'The date the operator chooses' },
  { value: StampSlotType.SIGNATORY_NAME, label: 'Signatory name', hint: 'From the signature profile' },
  { value: StampSlotType.DESIGNATION, label: 'Designation', hint: 'From the signature profile' },
  { value: StampSlotType.ISSUED_AT, label: 'Issued at', hint: 'Your chamber’s city' },
  { value: StampSlotType.ISSUED_AT_LINE, label: 'Issued at + date', hint: 'e.g. BRISBANE, 19/09/2026' },
  { value: StampSlotType.FREE_TEXT, label: 'Fixed text', hint: 'Anything that never changes' },
];

/**
 * The formats a certificate date is actually written in. Australian forms read DD/MM/YYYY
 * (§8 of the operating manual); the long forms appear on several FTA certificates where
 * the box is wide enough and an unambiguous month avoids the US reading of 09/07.
 */
const DATE_FORMATS = [
  { value: 'DD/MM/YYYY' },
  { value: 'DD-MM-YYYY' },
  { value: 'DD MMM YYYY' },
  { value: 'DD MMMM YYYY' },
  { value: 'D MMMM YYYY' },
] as const;

/** Today in that format, so the administrator picks by reading the result, not the tokens. */
function formatSample(pattern: string): string {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = String(now.getFullYear());
  return pattern
    .replace('MMMM', now.toLocaleString('en-AU', { month: 'long' }))
    .replace('MMM', now.toLocaleString('en-AU', { month: 'short' }))
    .replace('DD', dd)
    .replace(/\bD\b/, String(now.getDate()))
    .replace('MM', mm)
    .replace('YYYY', yyyy);
}

export function StampingDetail() {
  const { documentTypeId } = useParams<{ documentTypeId: string }>();
  const toast = useToast();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const [slots, setSlots] = useState<StampSlot[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  /** What each template page is (T-137). Untagged pages print only fixed-page marks. */
  const [pageKinds, setPageKinds] = useState<TemplatePageKinds>([]);
  const [stampOverleaf, setStampOverleaf] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [view, setView] = useState<'EDIT' | 'BOTH' | 'PROOF' | 'COMPARE'>('EDIT');
  const [pageSize, setPageSize] = useState({ width: 595.28, height: 841.89 });
  const [dragging, setDragging] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['stamping', documentTypeId],
    queryFn: () => api.get<StampingDetailData>(`/api/console/configuration/stamping/${documentTypeId}`),
    enabled: Boolean(documentTypeId),
  });

  // Local editing state, seeded from the server and only reset when the server data
  // genuinely changes — otherwise an in-progress edit would be wiped by a refetch.
  useEffect(() => {
    if (query.data?.config) {
      setSlots(query.data.config.slots);
      // A layout saved before page tags existed: its overleaf pages become overleaf tags.
      setPageKinds(
        withImpliedFace(query.data.config.templatePageKinds) ??
          Array.from({ length: Math.max(0, ...(query.data.config.overleafPages ?? [])) }, (_, i) =>
            query.data!.config!.overleafPages.includes(i + 1) ? ('OVERLEAF' as const) : null,
          ),
      );
      setStampOverleaf(query.data.config.stampOverleaf ?? false);
      setDirty(false);
    } else if (query.data) {
      setSlots([]);
      setPageKinds([]);
      setStampOverleaf(false);
      setDirty(false);
    }
  }, [query.data]);

  const save = useMutation({
    mutationFn: () =>
      api.post<{ configId: string }>(`/api/console/configuration/stamping/${documentTypeId}`, {
        name: query.data?.config?.name ?? `${query.data?.documentType.name} layout`,
        slots,
        overleafPages: pageKinds.flatMap((k, i) => (k === 'OVERLEAF' ? [i + 1] : [])),
        templatePageKinds: pageKinds.some(Boolean) ? pageKinds : null,
        stampOverleaf,
      }),
    onSuccess: () => {
      toast.success('Layout saved', 'Certificates already issued are unaffected.');
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ['stamping', documentTypeId] });
      void queryClient.invalidateQueries({ queryKey: ['stamping-overview'] });
    },
    onError: (error) =>
      toast.error(
        'Could not save the layout',
        error instanceof ApiError ? error.message : 'Something went wrong.',
      ),
  });

  const uploadTemplate = useMutation({
    mutationFn: async (file: File) =>
      api.post<{ pages: Array<{ width: number; height: number }>; proofAvailable: boolean }>(
        `/api/console/configuration/stamping/${documentTypeId}/template`,
        { filename: file.name, contentBase64: await toBase64(file) },
      ),
    onSuccess: (result) => {
      const size = `${result.pages.length} page(s) at ${Math.round(result.pages[0]!.width)} × ${Math.round(result.pages[0]!.height)} points.`;
      if (result.proofAvailable) {
        toast.success('Template uploaded', size);
      } else {
        // Placing marks still works — the canvas renders this file. Only the server-side
        // proof cannot, and an administrator who is not told that will read the empty
        // Proof pane as the feature being broken.
        toast.warn(
          'Template uploaded — the proof will not render',
          `${size} You can place marks against it, but this file cannot be written to, so the Proof pane will show an error. Re-saving it or printing it to PDF usually fixes that.`,
        );
      }
      void queryClient.invalidateQueries({ queryKey: ['stamping', documentTypeId] });
      void queryClient.invalidateQueries({ queryKey: ['stamping-overview'] });
    },
    onError: (error) =>
      toast.error('Could not upload the template', error instanceof ApiError ? error.message : ''),
  });

  const selected = slots.find((s) => s.id === selectedId) ?? null;

  const addSlot = (type: StampSlotType) => {
    const id = `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const config = SLOT_TYPES.find((t) => t.value === type)!;
    const slot: StampSlot = {
      id,
      slotType: type,
      label: config.label,
      page,
      // Derived from the page's tag when saved; the server decides, not the editor.
      pageKind: 'FIXED',
      // Dropped somewhere visible and sane rather than at the origin, which on a PDF is
      // the bottom-left corner and reads as "it did not work".
      x: 120,
      y: 200,
      width: type === StampSlotType.SEAL || type === StampSlotType.SIGNATURE ? DEFAULT_IMAGE_MARK_WIDTH : null,
      height: null,
      rotation: 0,
      opacity: 1,
      fontFamily: 'Helvetica',
      fontSize: type === StampSlotType.CERTIFICATE_NUMBER ? 11 : 9,
      fontColor: '#000000',
      align: 'LEFT',
      staticText: null,
      dateFormat: 'DD/MM/YYYY',
      defaultSealId: type === StampSlotType.SEAL ? (query.data?.availableSeals[0]?.id ?? null) : null,
      required: true,
      sortOrder: 0,
    };
    // Into its layer, not on top of everything: seals at the back, then signatures, then
    // text in front — so a seal added last does not cover the date (T-126).
    setSlots((prev) => {
      const next = [...prev];
      next.splice(layerInsertIndex(prev, type), 0, slot);
      return next;
    });
    setSelectedId(id);
    setDirty(true);
  };

  /**
   * Retyping an existing mark, rather than making the administrator delete it and place a
   * new one in the same spot by eye. "Issued at + date" → "Certificate date" is a real and
   * common change: some forms carry the place and the date in one box, others keep the
   * date in its own numbered field.
   *
   * Position, font and colour are kept — they are what was fiddly to get right. Only the
   * settings that belong to the OLD type are dropped, because a seal id on a date mark is
   * meaningless and would be silently carried into the saved configuration.
   */
  const changeType = (slot: StampSlot, next: StampSlotType) => {
    const wasImage = slot.slotType === StampSlotType.SEAL || slot.slotType === StampSlotType.SIGNATURE;
    const isImage = next === StampSlotType.SEAL || next === StampSlotType.SIGNATURE;
    const oldLabel = SLOT_TYPES.find((s) => s.value === slot.slotType)?.label;

    patchSlot(slot.id!, {
      slotType: next,
      // A label the administrator never customised should follow the type, or a date mark
      // goes on calling itself "Issued at + date" in the Approve dialog.
      label: !slot.label || slot.label === oldLabel ? (SLOT_TYPES.find((s) => s.value === next)?.label ?? null) : slot.label,
      defaultSealId: next === StampSlotType.SEAL ? (query.data?.availableSeals[0]?.id ?? null) : null,
      staticText: next === StampSlotType.FREE_TEXT ? slot.staticText : null,
      // Width sizes an image; a text mark has none. Crossing that line needs a value, not
      // whatever the other kind left behind.
      width: isImage ? (wasImage ? slot.width : DEFAULT_IMAGE_MARK_WIDTH) : null,
      height: null,
    });
  };

  const patchSlot = useCallback((id: string, patch: Partial<StampSlot>) => {
    setSlots((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    setDirty(true);
  }, []);

  /**
   * Moves a mark one layer forward (drawn later, so in front) or back. The list order IS
   * the drawing order: it is saved as `sortOrder`, and the engine draws in it.
   */
  const moveLayer = useCallback((id: string, direction: 'forward' | 'back' | 'front') => {
    setSlots((prev) => {
      const from = prev.findIndex((s) => s.id === id);
      if (from < 0) return prev;
      const to =
        direction === 'front' ? prev.length - 1 : Math.min(prev.length - 1, Math.max(0, from + (direction === 'forward' ? 1 : -1)));
      if (to === from) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      return next;
    });
    setDirty(true);
  }, []);

  /** Drops the dragged layer at `targetId`'s position in the list. */
  const dropLayer = (draggedId: string, targetId: string) => {
    if (draggedId === targetId) return;
    setSlots((prev) => {
      const from = prev.findIndex((s) => s.id === draggedId);
      const to = prev.findIndex((s) => s.id === targetId);
      if (from < 0 || to < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      return next;
    });
    setDirty(true);
  };

  const measure = useTextMeasurer();
  const imageUrlFor = useCallback(
    (slot: StampSlot): string | null => {
      if (slot.slotType === StampSlotType.SEAL) {
        return query.data?.availableSeals.find((seal) => seal.id === slot.defaultSealId)?.imageUrl ?? null;
      }
      if (slot.slotType === StampSlotType.SIGNATURE) return query.data?.signatureUrl ?? null;
      return null;
    },
    [query.data],
  );
  const imageSizes = useImageSizes([
    ...(query.data?.availableSeals.map((seal) => seal.imageUrl) ?? []),
    query.data?.signatureUrl,
  ]);

  /** Every mark's box, from the shared function the engine draws with (T-125). */
  const layouts = useMemo(() => {
    const out: Record<string, MarkLayout> = {};
    if (!query.data) return out;
    for (const slot of slots) {
      out[slot.id ?? ''] = markLayout(slot, {
        page: pageSize,
        imageUrl: imageUrlFor,
        imageSizes,
        measure,
        sample: query.data.sampleInputs,
      });
    }
    return out;
  }, [slots, pageSize, imageUrlFor, imageSizes, measure, query.data]);

  /** Dates and signatures hidden by something drawn in front of them. */
  const covered = useMemo(
    () =>
      coveredMarks(
        slots.map((slot) => ({ ...slot, id: slot.id ?? '', box: layouts[slot.id ?? '']?.box ?? null })),
      ),
    [slots, layouts],
  );
  const coveredIds = useMemo(() => new Set(covered.map((c) => c.covered.id)), [covered]);

  const selectedLayout = selected ? layouts[selected.id ?? ''] : undefined;

  const proofAvailable = Boolean(query.data?.templateUrl) && slots.length > 0;
  const proof = useDraftProof({
    documentTypeId: documentTypeId ?? '',
    slots,
    layouts,
    enabled: proofAvailable && view !== 'EDIT',
    pages: { templatePageKinds: pageKinds.some(Boolean) ? pageKinds : null, stampOverleaf },
  });

  /** Tag a template page. Marks already on it keep their place; the tag decides where they print. */
  const setPageKind = (n: number, kind: PageKind | null) => {
    const tagged = [...pageKinds];
    while (tagged.length < n) tagged.push(null);
    tagged[n - 1] = kind;
    while (tagged.length && tagged[tagged.length - 1] === null) tagged.pop();
    // Tagging a continuation sheet makes an untagged page 1 the face, as the server does.
    const next = withImpliedFace(tagged) ?? [];
    setPageKinds(next);
    setDirty(true);
    if (next[0] === 'CERTIFICATE' && tagged[0] !== 'CERTIFICATE') {
      toast.info('Page 1 is the certificate face', 'Its marks are the ones continuation sheets start from.');
    }
    // A continuation sheet carries the face's marks a little moved, so start it from them
    // rather than from nothing. Only when it is empty: never overwrite placed marks.
    if (
      kind === 'CONTINUATION' &&
      primaryTemplatePage('CONTINUATION', next) === n &&
      !slots.some((s) => s.page === n)
    ) {
      copyFromFace(n, next);
    }
  };

  /**
   * "Copy marks from the certificate face" (T-137): the continuation sheet starts as a copy of
   * the face's layout, then the administrator nudges each mark to this sheet's boxes.
   */
  const copyFromFace = (target: number, kinds: TemplatePageKinds = pageKinds) => {
    const face = primaryTemplatePage('CERTIFICATE', kinds);
    if (!face || !slots.some((s) => s.page === face)) return;
    setSlots((prev) => [
      ...prev.filter((s) => s.page !== target),
      ...prev
        .filter((s) => s.page === face)
        .map((s) => ({ ...s, id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, page: target })),
    ]);
    setDirty(true);
    toast.success('Marks copied', 'Now move each one onto this sheet\'s boxes.');
  };

  if (query.isLoading) return <Skeleton className="h-[40rem]" />;
  if (!query.data) {
    return (
      <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title="Not found">
        <Link to="/configuration/stamping" className="underline">Back to stamping</Link>
      </Callout>
    );
  }

  const data = query.data;
  /** The proof needs a template to render onto — not a saved layout: it proofs the one on screen. */
  const showProof = (view === 'BOTH' || view === 'PROOF') && proofAvailable;
  const compareUrl = view === 'COMPARE' && proof.current ? proof.url : null;
  /** Top of the Layers list is in front, so the list is the drawing order reversed. */
  const layersFrontFirst = [...slots].reverse();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <Link
            to="/configuration/stamping"
            className="inline-flex items-center gap-1.5 [font-size:var(--pv-text-xs)] text-[var(--pv-muted)] hover:text-[var(--pv-ink)]"
          >
            <ArrowLeft aria-hidden className="size-3.5" /> All document types
          </Link>
          <div className="mt-2 flex flex-wrap items-center gap-2.5">
            <h1 className="[font-size:var(--pv-text-h1)] font-bold tracking-[-0.02em]">
              {data.documentType.name}
            </h1>
            <CodeChip tone="leaf">{data.documentType.code}</CodeChip>
            {dirty && <Badge tone="warn" size="sm" dot>Unsaved changes</Badge>}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInput}
            type="file"
            accept="application/pdf"
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) uploadTemplate.mutate(file);
            }}
            aria-label="Choose the blank template PDF"
          />
          <Button
            variant="secondary"
            leadingIcon={<Upload className="size-3.5" />}
            loading={uploadTemplate.isPending}
            onClick={() => fileInput.current?.click()}
          >
            {data.templateUrl ? 'Replace template' : 'Upload template'}
          </Button>
          {proofAvailable && (
            // The proof used to be a PDF downloaded into another tab, so checking a nudge
            // meant leaving the screen and coming back. It renders here now; this chooses
            // how much room to give it.
            <div
              role="group"
              aria-label="What to show"
              className="inline-flex overflow-hidden rounded-[var(--pv-radius-sm)] border border-[var(--pv-line-strong)]"
            >
              {(['EDIT', 'BOTH', 'PROOF', 'COMPARE'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={view === mode}
                  onClick={() => setView(mode)}
                  className={
                    view === mode
                      ? 'bg-[var(--pv-brand)] px-3 py-1.5 [font-size:12.5px] font-medium text-[var(--pv-brand-on)]'
                      : 'bg-[var(--pv-card)] px-3 py-1.5 [font-size:12.5px] text-[var(--pv-muted)] hover:bg-[var(--pv-card-alt)]'
                  }
                >
                  {mode === 'EDIT' ? 'Edit' : mode === 'BOTH' ? 'Both' : mode === 'PROOF' ? 'Proof' : 'Compare'}
                </button>
              ))}
            </div>
          )}
          <Button
            variant="primary"
            leadingIcon={<Save className="size-3.5" />}
            disabled={!dirty || slots.length === 0}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save layout
          </Button>
        </div>
      </div>

      <div
        className={
          showProof
            ? 'grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_320px]'
            : 'grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px]'
        }
      >
        {view !== 'PROOF' && (
        <div className="flex min-w-0 flex-col gap-2">
          {view === 'COMPARE' && !compareUrl && (
            <p className="[font-size:12px] text-[var(--pv-muted)]">
              Rendering the proof to compare against…
            </p>
          )}
          {view === 'COMPARE' && compareUrl && proof.sync.kind === 'in-sync' && (
            <Badge tone="ok" size="sm" dot className="w-fit">
              In sync · {proof.sync.checked} mark{proof.sync.checked === 1 ? '' : 's'} within 0.5pt of the proof
            </Badge>
          )}
          {view === 'COMPARE' && compareUrl && proof.sync.kind === 'out-of-sync' && (
            <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title={`Out of sync: ${proof.sync.label}`}>
              The dashed outline and the printed mark do not line up. Trust the proof — it is what
              prints. This is a fault on our side, not in your layout.
            </Callout>
          )}
        <StampCanvas
          templateUrl={data.templateUrl}
          compareUrl={compareUrl}
          pageWidth={data.config?.templatePageWidth ?? null}
          pageHeight={data.config?.templatePageHeight ?? null}
          slots={slots}
          layouts={layouts}
          coveredIds={coveredIds}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onChange={patchSlot}
          onLayer={moveLayer}
          onPageSize={setPageSize}
          page={page}
          onPageChange={setPage}
          onUploadTemplate={() => fileInput.current?.click()}
          pageKinds={pageKinds}
          onPageKindChange={setPageKind}
          stampOverleaf={stampOverleaf}
          onStampOverleafChange={(allowed) => {
            setStampOverleaf(allowed);
            setDirty(true);
          }}
          onCopyFromFace={copyFromFace}
        />
        </div>
        )}

        {showProof && (
          <ProofPane
            url={proof.url}
            loading={proof.loading}
            error={proof.error}
            current={proof.current}
            sync={proof.sync}
            onRender={() => void proof.render()}
          />
        )}

        <div className="flex flex-col gap-4">
          {covered.length > 0 && (
            <Callout tone="warn" icon={<AlertTriangle className="size-4" />} title="A mark is hidden">
              <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                {covered.map(({ covered: hidden, by }) => (
                  <li key={hidden.id}>
                    <b>{hidden.label ?? hidden.slotType}</b> is partly covered by{' '}
                    <b>{by.label ?? by.slotType}</b>, which prints in front of it. Move one of them
                    apart, or{' '}
                    <button
                      type="button"
                      className="font-semibold underline underline-offset-2"
                      onClick={() => moveLayer(hidden.id, 'front')}
                    >
                      bring {hidden.label ?? 'it'} to front
                    </button>{' '}
                    — though then it covers the other one.
                  </li>
                ))}
              </ul>
            </Callout>
          )}

          <Card>
            <CardHeader
              title="Layers"
              hint={`${slots.length}`}
              description="Top of the list prints in front. Drag to reorder, or select a mark and press ] or [."
            />
            {slots.length === 0 ? (
              <p className="[font-size:13px] text-[var(--pv-muted)]">
                Nothing placed yet. Add a certificate number first — every certificate must carry
                one, or it cannot be verified.
              </p>
            ) : (
              <>
                <p className="mb-1 [font-size:var(--pv-text-xs)]  text-[var(--pv-faint)]">Front</p>
                <ul className="flex list-none flex-col gap-0 p-0" aria-label="Layers, front first">
                  {layersFrontFirst.map((slot, index) => (
                    <li
                      key={slot.id}
                      draggable
                      onDragStart={(e) => {
                        setDragging(slot.id ?? null);
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragOver={(e) => {
                        if (dragging) e.preventDefault();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragging && slot.id) dropLayer(dragging, slot.id);
                        setDragging(null);
                      }}
                      onDragEnd={() => setDragging(null)}
                      className={
                        'flex items-center gap-1 border-b border-[var(--pv-line)] last:border-b-0 ' +
                        (selectedId === slot.id ? 'bg-[var(--pv-brand-soft)] ' : '') +
                        (dragging === slot.id ? 'opacity-50' : '')
                      }
                    >
                      <GripVertical aria-hidden className="size-3.5 shrink-0 cursor-grab text-[var(--pv-faint)]" />
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedId(slot.id ?? null);
                          setPage(slot.page);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === ']' || e.key === '[') {
                            e.preventDefault();
                            moveLayer(slot.id ?? '', e.key === ']' ? 'forward' : 'back');
                          }
                        }}
                        aria-pressed={selectedId === slot.id}
                        aria-label={`${slot.label ?? slot.slotType}, layer ${index + 1} of ${slots.length} from the front`}
                        className="flex min-w-0 flex-1 items-center gap-2 px-1 py-2 text-left hover:bg-[var(--pv-paper-sunk)]"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5 truncate [font-size:13px] font-medium">
                            {slot.label ?? slot.slotType}
                            {coveredIds.has(slot.id ?? '') && (
                              <AlertTriangle aria-label="Partly covered" className="size-3 shrink-0 text-[var(--pv-warn)]" />
                            )}
                          </span>
                          <span className="pv-num block [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                            p{slot.page} · {slot.x}, {slot.y}
                          </span>
                        </span>
                        <SlotTypeBadge type={slot.slotType} />
                      </button>
                    </li>
                  ))}
                </ul>
                <p className="mt-1 [font-size:var(--pv-text-xs)]  text-[var(--pv-faint)]">Back</p>
              </>
            )}

            <div className="mt-3 border-t border-[var(--pv-line)] pt-3">
              <Field label="Add a mark">
                <NativeSelect
                  value=""
                  onChange={(e) => {
                    if (e.target.value) addSlot(e.target.value as StampSlotType);
                    e.target.value = '';
                  }}
                >
                  <option value="">Choose what to add…</option>
                  {SLOT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label} — {type.hint}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            </div>
          </Card>

          {selected && (
            <Card>
              <CardHeader
                title={selected.label ?? selected.slotType}
                actions={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[var(--pv-stop)]"
                    leadingIcon={<Trash2 className="size-3.5" />}
                    onClick={() => {
                      setSlots((prev) => prev.filter((s) => s.id !== selected.id));
                      setSelectedId(null);
                      setDirty(true);
                    }}
                  >
                    Remove
                  </Button>
                }
              />
              <div className="flex flex-col gap-3">
                <Field
                  label="Type"
                  help="What this mark prints. Changing it keeps the position you have already set."
                >
                  <NativeSelect
                    value={selected.slotType}
                    onChange={(e) => changeType(selected, e.target.value as StampSlotType)}
                  >
                    {SLOT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label} — {type.hint}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>

                <Field label="Label" help="What this mark is called in the Approve dialog.">
                  <Input
                    value={selected.label ?? ''}
                    onChange={(e) => patchSlot(selected.id!, { label: e.target.value })}
                  />
                </Field>

                {selected.slotType === StampSlotType.SEAL && (
                  <Field label="Default seal" help="Pre-selected at approval; an operator can swap it.">
                    <NativeSelect
                      value={selected.defaultSealId ?? ''}
                      onChange={(e) => patchSlot(selected.id!, { defaultSealId: e.target.value || null })}
                    >
                      <option value="">Choose a seal…</option>
                      {data.availableSeals.map((seal) => (
                        <option key={seal.id} value={seal.id}>
                          {seal.name}
                        </option>
                      ))}
                    </NativeSelect>
                  </Field>
                )}

                {selected.slotType === StampSlotType.FREE_TEXT && (
                  <Field label="Text" required>
                    <Input
                      value={selected.staticText ?? ''}
                      onChange={(e) => patchSlot(selected.id!, { staticText: e.target.value })}
                    />
                  </Field>
                )}

                {(selected.slotType === StampSlotType.DATE ||
                  selected.slotType === StampSlotType.ISSUED_AT_LINE) && (
                  <Field
                    label="Date format"
                    help="Written the way it reads on the certificate. The proof shows the result."
                  >
                    <NativeSelect
                      value={selected.dateFormat}
                      onChange={(e) => patchSlot(selected.id!, { dateFormat: e.target.value })}
                    >
                      {DATE_FORMATS.map((f) => (
                        <option key={f.value} value={f.value}>
                          {f.value} — {formatSample(f.value)}
                        </option>
                      ))}
                    </NativeSelect>
                  </Field>
                )}

                {isImageMark(selected.slotType) && (
                  <Field
                    label="Width (points)"
                    help={
                      selectedLayout && !selectedLayout.provisional
                        ? `Prints ${Math.round(selectedLayout.box.width)} × ${Math.round(selectedLayout.box.height)} pt. Height follows the image, so it is never squashed. Or drag a corner on the page.`
                        : "Height follows the image's proportions. Or drag a corner on the page."
                    }
                  >
                    <Input
                      type="number"
                      min={12}
                      value={selected.width ?? DEFAULT_IMAGE_MARK_WIDTH}
                      onChange={(e) => patchSlot(selected.id!, { width: Number(e.target.value) || null, height: null })}
                    />
                  </Field>
                )}

                {!isImageMark(selected.slotType) && (
                  <Field
                    label="Box width (points)"
                    help={
                      selected.width
                        ? 'Alignment places the text inside this box. Drag its left or right edge on the page to change it.'
                        : 'Fits the text. Drag its left or right edge on the page to give it a fixed box — useful for centring in a form field.'
                    }
                  >
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={12}
                        placeholder="Fits the text"
                        value={selected.width ?? ''}
                        onChange={(e) => {
                          const width = Number(e.target.value);
                          patchSlot(selected.id!, width > 0 ? { width, x: selectedLayout?.box.x ?? selected.x } : fitTextToContent(selected, selectedLayout!));
                        }}
                      />
                      {selected.width && selectedLayout && (
                        <Button variant="ghost" size="sm" onClick={() => patchSlot(selected.id!, fitTextToContent(selected, selectedLayout))}>
                          Fit to text
                        </Button>
                      )}
                    </div>
                  </Field>
                )}

                <Field label="Layer" help="Marks further forward print over those behind them.">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      leadingIcon={<ArrowUp className="size-3.5" />}
                      disabled={slots[slots.length - 1]?.id === selected.id}
                      onClick={() => moveLayer(selected.id!, 'forward')}
                    >
                      Bring forward
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      leadingIcon={<ArrowDown className="size-3.5" />}
                      disabled={slots[0]?.id === selected.id}
                      onClick={() => moveLayer(selected.id!, 'back')}
                    >
                      Send back
                    </Button>
                  </div>
                </Field>

                {selected.slotType !== StampSlotType.SEAL &&
                  selected.slotType !== StampSlotType.SIGNATURE && (
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Font size">
                        <Input
                          type="number"
                          value={selected.fontSize}
                          onChange={(e) => patchSlot(selected.id!, { fontSize: Number(e.target.value) })}
                        />
                      </Field>
                      <Field label="Align">
                        <NativeSelect
                          value={selected.align}
                          onChange={(e) => patchSlot(selected.id!, { align: e.target.value as never })}
                        >
                          <option value="LEFT">Left</option>
                          <option value="CENTER">Centre</option>
                          <option value="RIGHT">Right</option>
                        </NativeSelect>
                      </Field>
                      {/* Per MARK, not per chamber: on a TAFTA certificate the "issued at"
                          line is red while the number beside it stays black. */}
                      <div className="col-span-2">
                        <ColourField
                          value={selected.fontColor}
                          onChange={(hex) => patchSlot(selected.id!, { fontColor: hex })}
                        />
                      </div>
                    </div>
                  )}

                <div className="grid grid-cols-2 gap-3">
                  <Field label="X (points)" help="From the left edge.">
                    <Input
                      type="number"
                      value={selected.x}
                      onChange={(e) => patchSlot(selected.id!, { x: Number(e.target.value) })}
                    />
                  </Field>
                  <Field label="Y (points)" help="From the BOTTOM edge.">
                    <Input
                      type="number"
                      value={selected.y}
                      onChange={(e) => patchSlot(selected.id!, { y: Number(e.target.value) })}
                    />
                  </Field>
                </div>

                <Field label="Required" help="Blocks approval if this mark cannot be resolved.">
                  <NativeSelect
                    value={selected.required ? 'yes' : 'no'}
                    onChange={(e) => patchSlot(selected.id!, { required: e.target.value === 'yes' })}
                  >
                    <option value="yes">Yes — a certificate is invalid without it</option>
                    <option value="no">No — print it when available</option>
                  </NativeSelect>
                </Field>
              </div>
            </Card>
          )}

          <Card className="!bg-[var(--pv-card-alt)]">
            <p className="pv-eyebrow text-[var(--pv-faint)]">Before this can issue</p>
            {/* Exactly the server's own conditions — an active config and a number
                format. A checklist that asks for more than approval does teaches an
                administrator to distrust it. */}
            <ul className="mt-2 flex list-none flex-col gap-1.5 p-0 [font-size:12.5px]">
              {[
                ['At least one mark', slots.length > 0],
                [
                  'Exactly one certificate number on the certificate face',
                  slots.filter(
                    (s) =>
                      s.slotType === StampSlotType.CERTIFICATE_NUMBER &&
                      (!pageKinds.includes('CERTIFICATE') || (pageKinds[s.page - 1] ?? 'CERTIFICATE') === 'CERTIFICATE'),
                  ).length === 1,
                ],
                [
                  stampOverleaf ? 'Overleaf marks allowed' : 'No marks on an overleaf page',
                  stampOverleaf || !slots.some((s) => pageKinds[s.page - 1] === 'OVERLEAF'),
                ],
                [
                  'Every required seal has a default',
                  !slots.some((s) => s.slotType === StampSlotType.SEAL && s.required && !s.defaultSealId),
                ],
              ].map(([label, ok]) => (
                <li key={label as string} className="flex items-center gap-2">
                  {ok ? (
                    <CheckCircle2 aria-hidden className="size-3.5 shrink-0 text-[var(--pv-ok)]" />
                  ) : (
                    <AlertTriangle aria-hidden className="size-3.5 shrink-0 text-[var(--pv-warn)]" />
                  )}
                  <span className={ok ? 'text-[var(--pv-muted)]' : 'font-medium'}>{label as string}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}

/**
 * Send every issued certificate straight to the printer.
 *
 * Lives here because this is the page about what happens when a certificate is stamped.
 * Off by default: a print dialog nobody asked for interrupts whatever the operator was
 * doing, and most chambers deliver electronically.
 *
 * The supporting documents are deliberately excluded. They are stamped, but the thing a
 * chamber files and couriers is the certificate.
 */
function PrintOnIssueCard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useSession();
  const isAdmin = user?.role === UserRole.CHAMBER_ADMIN;

  const query = useQuery({
    queryKey: ['issue-preferences'],
    queryFn: () => api.get<ChamberIssuePreferences>('/api/console/configuration/issue-preferences'),
  });

  const save = useMutation({
    mutationFn: (printCertificateOnIssue: boolean) =>
      api.patch<ChamberIssuePreferences>('/api/console/configuration/issue-preferences', {
        printCertificateOnIssue,
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(['issue-preferences'], result);
      toast.success(
        result.printCertificateOnIssue ? 'Printing on issue is on' : 'Printing on issue is off',
        result.printCertificateOnIssue
          ? 'The print dialog opens as soon as a certificate finishes stamping.'
          : 'Certificates are no longer sent to the printer automatically.',
      );
    },
    onError: (error) =>
      toast.error('Could not save that', error instanceof ApiError ? error.message : ''),
  });

  if (query.isLoading || !query.data) return null;

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-[24ch] flex-1">
          <p className="[font-size:var(--pv-text-sm)] font-semibold">Print on issue</p>
          <p className="mt-1 [font-size:13px] text-[var(--pv-muted)]">
            Open the print dialog as soon as a certificate has finished stamping, without anyone
            having to go and find it. The supporting documents are not printed.
            {!isAdmin && ' Only a chamber administrator can change this.'}
          </p>
        </div>
        <Switch
          checked={query.data.printCertificateOnIssue}
          disabled={!isAdmin || save.isPending}
          onCheckedChange={(next) => save.mutate(next)}
          label={query.data.printCertificateOnIssue ? 'On' : 'Off'}
        />
      </div>
    </Card>
  );
}
